import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { AxiosError } from 'axios';
import React, { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageWrapper, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import { useCreateUser } from '../../../queries';
import { UserDataFirebaseRequest } from '../../../types';
import { CreateUserForm } from '../components';

/** Kod z backendu (`v3-createUser`): konto jest w Auth, ale nie ma `users/{uid}`. */
const AUTH_ACCOUNT_WITHOUT_PROFILE = 'AUTH_ACCOUNT_WITHOUT_PROFILE';

type ExistingAccount = {
  code?: string;
  uid?: string;
  email?: string | null;
  phoneNumber?: string | null;
};

type CreateUserErrorBody = {
  message?: string;
  data?: ExistingAccount;
};

export const CreateUser: FC = () => {
  const { showPromise, showError } = useHeroToast();
  const { mutate, isPending } = useCreateUser();
  const navigate = useNavigate();

  // Konto gościa znalezione w Auth — czeka na zgodę superadmina na promocję.
  const [existingAccount, setExistingAccount] = useState<ExistingAccount | null>(null);
  const pendingFormData = useRef<UserDataFirebaseRequest | null>(null);

  // `isRetry` = to już jest ponowienie z `promoteExisting`, więc kolejny konflikt
  // pokazujemy jako błąd, a nie jako drugi monit (żeby nie zapętlić pytania).
  const submit = (data: UserDataFirebaseRequest, isRetry = false): void => {
    showPromise(
      () => new Promise(
        (resolve, reject) => {
          mutate(
            { data },
            {
              onSuccess: resolve,
              onError: reject,
            },
          );
        },
      ),
      <Spinner />,
    ).then(() => {
      navigate('/users');
    }).catch((err: AxiosError<CreateUserErrorBody>) => {
      const body = err?.response?.data;
      // Konto istnieje w Auth, ale nie jest kontem personelu (gość z aplikacji) —
      // nie tworzymy duplikatu, tylko pytamy, czy dopisać go do `users`.
      // Backend sprzed wdrożenia tej obsługi zwraca 409 BEZ kodu — wtedy też pytamy,
      // zamiast kończyć czerwonym toastem „e-mail już istnieje".
      const isGuestConflict = err?.response?.status === 409
        && (body?.data?.code === AUTH_ACCOUNT_WITHOUT_PROFILE || !body?.data?.code);

      if (isGuestConflict && !isRetry) {
        pendingFormData.current = data;
        setExistingAccount(body?.data ?? { email: data.email });
        return;
      }
      showError(body?.message || err.message || 'Request Error');
    });
  };

  const closePromotionModal = (): void => {
    setExistingAccount(null);
    pendingFormData.current = null;
  };

  const confirmPromotion = (): void => {
    const data = pendingFormData.current;
    setExistingAccount(null);
    pendingFormData.current = null;
    if (data) submit({ ...data, promoteExisting: true }, true);
  };

  return (
    <PageWrapper>
      <CreateUserForm onSubmit={submit} />
      <div className="flex justify-end gap-3 mt-6 pr-6 pb-6">
        <Button
          color="default"
          variant="light"
          onPress={() => navigate('/users')}
          isDisabled={isPending}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          type="submit"
          form="form-create-user"
          isLoading={isPending}
          isDisabled={isPending}
        >
          {isPending ? 'Creating...' : 'Create User'}
        </Button>
      </div>

      <Modal isOpen={!!existingAccount} onOpenChange={(open) => { if (!open) closePromotionModal(); }} size="md">
        <ModalContent>
          <ModalHeader>Ten użytkownik posiada konto gościa</ModalHeader>
          <ModalBody>
            <p className="text-sm">
              {existingAccount?.email || existingAccount?.phoneNumber || existingAccount?.uid}
              {' '}
              ma już konto gościa. Czy chcesz dodać go do grupy users?
            </p>
            <p className="text-sm text-gray-500">
              Nie powstanie drugie konto — do istniejącego logowania dopisujemy profil
              personelu (`users`) i uprawnienia z tego formularza.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={closePromotionModal} isDisabled={isPending}>
              Anuluj
            </Button>
            <Button color="primary" onPress={confirmPromotion} isLoading={isPending}>
              Tak, dodaj do users
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
};
